import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Body2, ScrollElement, ScrollShadow, List, InfoIcon, ListSeparator } from "@activecollab/components";
import PropTypes from "prop-types";
import { makeIsLoggedUserPowerUser } from "~/store/selectors/selector.users";
import { ParticipantItem } from "./ParticipantItem.jsx";
import { removeConversationAdmin, addConversationAdmin } from "../../../../../store/actions/actions.conversations.jsx";
import {
  makeGetConversationById,
  makeGetConversationParticipants,
} from "../../../../../store/selectors/selector.conversations.jsx";
import { useConversationContext } from "../../../context/index.jsx";
import { useConfirmDialogOption } from "../../../MenuOptions/index.jsx";

export const ParticipantsBody = ({ conversationId, onLeaveSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [participantId, setParticipantId] = useState(0);
  const { leave, removeParticipant } = useConversationContext();
  const [onShowLeaveDialog, leaveDialogTemplate] = useConfirmDialogOption({
    onConfirmSuccess: onLeaveSuccess,
    onConfirmAction: leave,
  });
  const getConversation = useMemo(() => makeGetConversationById(conversationId), [conversationId]);
  const conversation = useSelector(getConversation);
  const getIsPowerUser = useMemo(() => makeIsLoggedUserPowerUser(), []);
  const isPowerUser = useSelector(getIsPowerUser);

  const onRemoveParticipant = useCallback(() => {
    typeof removeParticipant === "function" && participantId > 0 && removeParticipant(participantId);
  }, [removeParticipant, participantId]);

  const [onShowRemoveDialog, removeDialogTemplate] = useConfirmDialogOption({
    onConfirmAction: onRemoveParticipant,
    dialogTitle: t("Remove from conversation?"),
    dialogContent: t("By removing someone from conversation, they will no longer be able to send or receive messages."),
    confirmBtnText: t("Remove"),
  });

  const getConversationParticipants = useMemo(() => {
    return makeGetConversationParticipants(conversationId);
  }, [conversationId]);
  const conversationParticipants = useSelector(getConversationParticipants);

  const handleAddConversationAdmin = useCallback(
    (userId) => {
      dispatch(addConversationAdmin(conversationId, userId));
    },
    [dispatch, conversationId]
  );

  const handleRemoveConversationAdmin = useCallback(
    (userId) => {
      dispatch(removeConversationAdmin(conversationId, userId));
    },
    [dispatch, conversationId]
  );

  const handleShowRemoveParticipantDialog = useCallback(
    (userId) => {
      onShowRemoveDialog();
      setParticipantId(userId);
    },
    [onShowRemoveDialog]
  );

  return (
    <div className="tw-flex tw-overflow-y-auto tw-flex-1 tw-grow tw-h-full tw-w-full">
      <ScrollShadow className="tw-flex-1 tw-min-w-0 shadow-list-initial-content-body">
        {({ onScroll }) => (
          <ScrollElement
            className="tw-h-full"
            onScroll={onScroll}
          >
            <div className="tw-p-2">
              {conversation.parent_type === "Team" ? (
                <>
                  <div className="tw-pt-4 tw-px-4 tw-flex tw-gap-4">
                    <InfoIcon
                      className="tw-flex-shrink-0 tw-text-theme-700"
                      fill="currentColor"
                    />
                    <Body2
                      color="tertiary"
                      align="left"
                    >
                      {t("This is a team chat. Participants are added on teams page.")}{" "}
                      {isPowerUser ? (
                        <Link
                          to="/teams"
                          className="tw-no-underline tw-truncate tw-text-primary"
                        >
                          {t("Visit Teams")}
                        </Link>
                      ) : null}
                    </Body2>
                  </div>
                  <ListSeparator />
                </>
              ) : null}

              {conversationParticipants.admins.length > 0 ? (
                <div>
                  {conversationParticipants.participants.length > 0 ? (
                    <Body2
                      color="tertiary"
                      weight="bold"
                      align="left"
                      className="tw-px-2 tw-pb-2 tw-pt-4"
                    >
                      {t("Admins")}
                    </Body2>
                  ) : null}
                  <List>
                    {conversationParticipants.admins.map(({ id, name, avatarUrl }) => {
                      return (
                        <ParticipantItem
                          key={id}
                          id={id}
                          avatarUrl={avatarUrl}
                          name={name}
                          onRemoveConversationAdmin={handleRemoveConversationAdmin}
                          isAdmin
                          onRemove={handleShowRemoveParticipantDialog}
                          onLeave={onShowLeaveDialog}
                        />
                      );
                    })}
                  </List>
                </div>
              ) : null}
              {conversationParticipants.participants.length > 0 ? (
                <div>
                  {conversationParticipants.admins.length > 0 ? (
                    <Body2
                      color="tertiary"
                      weight="bold"
                      align="left"
                      className="tw-px-2 tw-pb-2 tw-pt-4"
                    >
                      {t("Participants")}
                    </Body2>
                  ) : null}
                  <List>
                    {conversationParticipants.participants.map(({ id, name, avatarUrl }) => {
                      return (
                        <ParticipantItem
                          key={id}
                          id={id}
                          avatarUrl={avatarUrl}
                          name={name}
                          onAddConversationAdmin={handleAddConversationAdmin}
                          onRemove={handleShowRemoveParticipantDialog}
                          onLeave={onShowLeaveDialog}
                        />
                      );
                    })}
                  </List>
                </div>
              ) : null}
            </div>
          </ScrollElement>
        )}
      </ScrollShadow>
      {leaveDialogTemplate}
      {removeDialogTemplate}
    </div>
  );
};

ParticipantsBody.propTypes = {
  conversationId: PropTypes.number.isRequired,
  onLeaveSuccess: PropTypes.func,
};
